<template>
  <Teleport to="body">
    <div
      class="toast-container"
      role="region"
      :aria-label="t('notifications.region_label')"
      aria-live="polite"
    >
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="`toast--${toast.variant}`"
          role="status"
        >
          <span class="toast-icon" aria-hidden="true">
            <svg v-if="toast.variant === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
            </svg>
            <svg v-else-if="toast.variant === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <svg v-else-if="toast.variant === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <span class="toast-message">{{ toast.message }}</span>
          <button
            v-if="toast.action"
            type="button"
            class="toast-action"
            @click="handleAction(toast)"
          >
            {{ toast.action.label }}
          </button>
          <button
            type="button"
            class="toast-close"
            :aria-label="closeLabel"
            @click="dismiss(toast.id)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { type Toast, useToast } from "../composables/useToast"

const { toasts, dismiss } = useToast()
const { t } = useI18n()

const closeLabel = computed(() => t("common.close"))

function handleAction(toast: Toast) {
  toast.action?.handler()
  dismiss(toast.id)
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-4);
  padding-bottom: calc(var(--spacing-4) + var(--safe-bottom));
  pointer-events: none;
  z-index: var(--z-toast, 1200);
}

.toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  width: 100%;
  max-width: 420px;
  padding: 12px 14px;
  background: var(--surface);
  border: 1px solid var(--border-default);
  border-left-width: 3px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: var(--text-sm);
  line-height: 1.4;
}

.toast--success {
  border-left-color: var(--forest-500);
}

.toast--error {
  border-left-color: var(--ruby-500);
}

.toast--warning {
  border-left-color: var(--amber-500);
}

.toast--info {
  border-left-color: var(--steel-500);
}

.toast-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.toast-icon svg {
  width: 18px;
  height: 18px;
}

.toast--success .toast-icon {
  color: var(--forest-600);
}

.toast--error .toast-icon {
  color: var(--ruby-600);
}

.toast--warning .toast-icon {
  color: var(--amber-600);
}

.toast--info .toast-icon {
  color: var(--steel-600);
}

.toast-message {
  flex: 1;
  min-width: 0;
}

.toast-action {
  flex-shrink: 0;
  min-height: 32px;
  padding: 4px 10px;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--accent);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;
}

.toast-action:hover,
.toast-action:focus-visible {
  background: var(--accent-subtle);
  outline: none;
}

.toast-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-tertiary);
  cursor: pointer;
}

.toast-close svg {
  width: 14px;
  height: 14px;
}

.toast-close:hover,
.toast-close:focus-visible {
  color: var(--text-primary);
  background: var(--bg-secondary);
  outline: none;
}

.toast-enter-active,
.toast-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.toast-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.toast-leave-to {
  transform: translateY(8px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active {
    transition: opacity 0.15s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    transform: none;
  }
}

@media (min-width: 1024px) {
  .toast-container {
    align-items: flex-end;
    padding: var(--spacing-5);
  }
}
</style>
